import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  ArrowLeft, Activity, AlertTriangle, CheckCircle, Users, Building,
  RefreshCw, Database, XCircle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import Header from '@/components/Header';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DataHealthCheck = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);

  const runHealthCheck = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/data-health-check`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setReport(response.data);
      toast.success('Health check completed');
    } catch (error) {
      console.error('Health check error:', error);
      toast.error(error.response?.data?.detail || 'Failed to run health check');
    } finally {
      setLoading(false);
    }
  };

  const IssueCard = ({ title, count, items, color, fields }) => (
    <div className={`bg-white rounded-lg shadow-md p-4 border-l-4 ${color}`}>
      <div className="flex items-center justify-between mb-3">
        <h4 className="font-semibold text-gray-800">{title}</h4>
        <span className={`px-2 py-1 rounded text-sm font-bold ${count > 0 ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
          {count}
        </span>
      </div>
      {count > 0 && items && items.length > 0 && (
        <div className="max-h-40 overflow-y-auto">
          <table className="w-full text-xs">
            <thead className="bg-gray-100">
              <tr>
                {fields.map(f => <th key={f} className="px-2 py-1 text-left">{f}</th>)}
              </tr>
            </thead>
            <tbody>
              {items.slice(0, 20).map((item, idx) => (
                <tr key={idx} className="border-b">
                  {fields.map(f => (
                    <td key={f} className="px-2 py-1 truncate max-w-[120px]">
                      {item[f.toLowerCase().replace(' ', '_')] || '-'}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          {items.length > 20 && (
            <p className="text-xs text-gray-500 mt-2">...and {items.length - 20} more</p>
          )}
        </div>
      )}
      {count === 0 && (
        <p className="text-sm text-green-600 flex items-center">
          <CheckCircle size={14} className="mr-1" /> No issues found
        </p>
      )}
    </div>
  );

  const DistributionCard = ({ title, data, icon: Icon }) => (
    <div className="bg-white rounded-lg shadow-md p-4">
      <h4 className="font-semibold text-gray-800 mb-3 flex items-center">
        <Icon size={18} className="mr-2 text-blue-600" />
        {title}
      </h4>
      <div className="space-y-2">
        {Object.entries(data || {}).map(([key, value]) => (
          <div key={key} className="flex justify-between items-center">
            <span className={`text-sm ${key === '(empty/null)' ? 'text-red-600 font-semibold' : 'text-gray-700'}`}>
              {key}
            </span>
            <span className={`font-bold ${key === '(empty/null)' ? 'text-red-600' : 'text-gray-800'}`}>
              {value}
            </span>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <>
      <Header />
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 py-8">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center space-x-4">
              <Button variant="outline" onClick={() => navigate('/')} data-testid="back-button">
                <ArrowLeft className="mr-2" size={18} />
                Back
              </Button>
              <h1 className="text-3xl font-bold text-blue-700" data-testid="page-title">
                <Activity className="inline mr-3" size={32} />
                Data Health Check
              </h1>
            </div>
          </div>

          {/* Info Box */}
          <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg mb-6">
            <div className="flex items-start">
              <AlertTriangle className="text-yellow-600 mr-3 mt-1 flex-shrink-0" size={20} />
              <div className="text-sm text-yellow-700">
                <p className="font-semibold mb-1">Read-Only Health Check</p>
                <p>This tool analyzes your data for potential issues. It does <strong>NOT</strong> modify or delete any records. After reviewing the report, you can decide what actions to take.</p>
              </div>
            </div>
          </div>

          {/* Run Check Button */}
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <Button 
              onClick={runHealthCheck}
              disabled={loading}
              className="w-full py-6 text-lg bg-blue-600 hover:bg-blue-700"
              data-testid="run-check-btn"
            >
              {loading ? (
                <>
                  <RefreshCw className="animate-spin mr-2" size={20} />
                  Analyzing Data...
                </>
              ) : (
                <>
                  <Database className="mr-2" size={20} />
                  Run Health Check
                </>
              )}
            </Button>
          </div>

          {/* Results */}
          {report && (
            <>
              {/* Summary */}
              <div className="bg-white rounded-lg shadow-md p-6 mb-6">
                <h3 className="text-xl font-bold text-gray-800 mb-4">Summary</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-blue-50 p-4 rounded-lg text-center">
                    <p className="text-3xl font-bold text-blue-600">{report.summary?.total_members || 0}</p>
                    <p className="text-sm text-gray-600">Total Members</p>
                  </div>
                  <div className="bg-green-50 p-4 rounded-lg text-center">
                    <p className="text-3xl font-bold text-green-600">{report.summary?.total_advertisers || 0}</p>
                    <p className="text-sm text-gray-600">Total Advertisers</p>
                  </div>
                  <div className="bg-red-50 p-4 rounded-lg text-center">
                    <p className="text-3xl font-bold text-red-600">
                      {(report.summary?.members_missing_database_type || 0) + 
                       (report.summary?.advertisers_missing_magazine || 0)}
                    </p>
                    <p className="text-sm text-gray-600">Missing Magazine</p>
                  </div>
                  <div className="bg-orange-50 p-4 rounded-lg text-center">
                    <p className="text-3xl font-bold text-orange-600">
                      {(report.summary?.members_unusual_status || 0) + 
                       (report.summary?.advertisers_unusual_status || 0)}
                    </p>
                    <p className="text-sm text-gray-600">Unusual Status</p>
                  </div>
                </div>
              </div>

              {/* Distribution */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <DistributionCard 
                  title="Members by Magazine (database_type)" 
                  data={report.members?.by_database_type}
                  icon={Users}
                />
                <DistributionCard 
                  title="Members by Status" 
                  data={report.members?.by_status}
                  icon={Users}
                />
                <DistributionCard 
                  title="Advertisers by Magazine" 
                  data={report.advertisers?.by_magazine}
                  icon={Building}
                />
                <DistributionCard 
                  title="Advertisers by Status" 
                  data={report.advertisers?.by_status}
                  icon={Building}
                />
              </div>

              {/* Issues */}
              <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
                <XCircle className="mr-2 text-red-600" size={24} />
                Potential Issues
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <IssueCard 
                  title="Members - Missing Magazine (database_type)"
                  count={report.members?.missing_database_type?.length || 0}
                  items={report.members?.missing_database_type}
                  color="border-red-500"
                  fields={['member_id', 'name', 'contact_no']}
                />
                <IssueCard 
                  title="Members - Unusual Status"
                  count={report.members?.unusual_status?.length || 0}
                  items={report.members?.unusual_status}
                  color="border-orange-500"
                  fields={['member_id', 'name', 'status', 'database_type']}
                />
                <IssueCard 
                  title="Advertisers - Missing Magazine"
                  count={report.advertisers?.missing_magazine?.length || 0}
                  items={report.advertisers?.missing_magazine}
                  color="border-red-500"
                  fields={['advertiser_id', 'name', 'company_name']}
                />
                <IssueCard 
                  title="Advertisers - Unusual Status"
                  count={report.advertisers?.unusual_status?.length || 0}
                  items={report.advertisers?.unusual_status}
                  color="border-orange-500"
                  fields={['advertiser_id', 'name', 'status', 'magazine']}
                />
                <IssueCard 
                  title="Members - No Contact Info"
                  count={report.members?.missing_contact_info?.length || 0}
                  items={report.members?.missing_contact_info}
                  color="border-yellow-500"
                  fields={['member_id', 'name', 'database_type']}
                />
              </div>

              {/* Action Required */}
              <div className="mt-6 bg-blue-50 border border-blue-200 p-4 rounded-lg">
                <h4 className="font-semibold text-blue-700 mb-2">What to do next?</h4>
                <ul className="text-sm text-blue-700 list-disc list-inside space-y-1">
                  <li>Review the issues listed above</li>
                  <li>Members/Advertisers with missing magazine won't be filtered correctly</li>
                  <li>You can fix individual records by searching and editing them</li>
                  <li>If you need bulk fixes, let me know what changes you want</li>
                </ul>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default DataHealthCheck;
